import { useState, useEffect } from 'react'
import { AdminLayout } from '../../components/layouts/Layout'
import { Card, Button, Badge, Modal, Textarea } from '../../components/common/UI'
import { Check, X, Eye } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { advertisementService, businessService } from '../../services/api'

export default function AdApprovals() {
  const [advertisements, setAdvertisements] = useState<any[]>([])
  const [businesses, setBusinesses] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedAd, setSelectedAd] = useState<any>(null)
  const [showModal, setShowModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [adsData, businessesData] = await Promise.all([
        advertisementService.list(),
        businessService.list()
      ])
      setAdvertisements(adsData.filter((ad: any) => ad.approvalStatus === 'PENDING'))
      setBusinesses(businessesData)
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (adId: string) => {
    await advertisementService.update(adId, {
      approvalStatus: 'APPROVED',
      approvedAt: new Date().toISOString()
    })
    setShowModal(false)
    setSelectedAd(null)
    await loadData()
  }

  const handleReject = async (adId: string) => {
    if (!rejectionReason.trim()) return

    await advertisementService.update(adId, {
      approvalStatus: 'REJECTED',
      rejectionReason
    })
    setShowModal(false)
    setSelectedAd(null)
    setRejectionReason('')
    await loadData()
  }

  const openApproveModal = (ad: any) => {
    setSelectedAd(ad)
    setActionType('approve')
    setShowModal(true)
  }

  const openRejectModal = (ad: any) => {
    setSelectedAd(ad)
    setActionType('reject')
    setShowModal(true)
  }

  const getBusiness = (businessId: string) => {
    return businesses.find(b => b.id === businessId)
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading...</p>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Ad Approvals</h1>
            <p className="text-gray-600">{advertisements.length} ads pending review</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {advertisements.map((ad: any) => {
            const business = getBusiness(ad.businessId)
            return (
              <Card key={ad.id} className="flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">{ad.adTitle}</h3>
                    <p className="text-sm text-gray-600">{business?.businessName || ad.businessName}</p>
                  </div>
                  <Badge variant="warning">Pending</Badge>
                </div>

                {ad.companyPhotoUrls && ad.companyPhotoUrls.length > 0 && (
                  <div className="mb-4">
                    <img src={ad.companyPhotoUrls[0]} alt={ad.adTitle} className="w-full h-40 object-cover rounded-lg" />
                    {ad.companyPhotoUrls.length > 1 && (
                      <p className="text-xs text-gray-500 mt-1">+{ad.companyPhotoUrls.length - 1} more photos</p>
                    )}
                  </div>
                )}

                {ad.companyVideoUrl && (
                  <div className="mb-4">
                    <video src={ad.companyVideoUrl} className="w-full h-40 object-cover rounded-lg" controls />
                  </div>
                )}

                <p className="text-sm text-gray-700 mb-4 line-clamp-3">{ad.adContent}</p>

                <div className="mb-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium">
                      {formatters.date(ad.startDate)} - {formatters.date(ad.endDate)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Target Audience:</span>
                    <span className="font-medium">{ad.targetAudience?.length || 0} groups</span>
                  </div>
                </div>

                <div className="mt-auto flex space-x-2">
                  <Button size="sm" variant="outline" onClick={() => { setSelectedAd(ad); setShowModal(true); }} icon={Eye} className="flex-1">
                    View
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openApproveModal(ad)} icon={Check} className="flex-1">
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openRejectModal(ad)} icon={X} className="flex-1">
                    Reject
                  </Button>
                </div>
              </Card>
            )
          })}
        </div>

        {advertisements.length === 0 && (
          <Card className="text-center py-12">
            <Check className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
            <p className="text-gray-600">No ads pending approval</p>
          </Card>
        )}

        <Modal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedAd(null); setRejectionReason(''); }}
          title={actionType === 'approve' ? 'Approve Advertisement' : 'Reject Advertisement'}
        >
          {selectedAd && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">{selectedAd.adTitle}</h3>
                <p className="text-sm text-gray-600">{selectedAd.adContent}</p>
              </div>

              {selectedAd.companyPhotoUrls && selectedAd.companyPhotoUrls.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Photos ({selectedAd.companyPhotoUrls.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedAd.companyPhotoUrls.map((url: string, index: number) => (
                      <img key={index} src={url} alt={`Photo ${index + 1}`} className="w-full h-20 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}

              {selectedAd.companyVideoUrl && (
                <div>
                  <p className="text-sm font-medium mb-2">Video</p>
                  <video src={selectedAd.companyVideoUrl} className="w-full h-40 object-cover rounded" controls />
                </div>
              )}

              {actionType === 'reject' && (
                <Textarea
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  placeholder="Provide a reason for rejection..."
                />
              )}

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => { setShowModal(false); setSelectedAd(null); setRejectionReason(''); }}>
                  Cancel
                </Button>
                <Button
                  onClick={() => actionType === 'approve' ? handleApprove(selectedAd.id) : handleReject(selectedAd.id)}
                  variant={actionType === 'approve' ? 'primary' : 'danger'}
                >
                  {actionType === 'approve' ? 'Approve' : 'Reject'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AdminLayout>
  )
}